import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { Folder, Plus, Upload, Search, LayoutGrid, List, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import api from '../lib/api';
import CharacterCard from '../components/CharacterCard';
import FolderItem from '../components/FolderItem';
import CharacterDetail from '../components/CharacterDetail';
import ImportModal from '../components/ImportModal';

export default function Dashboard() {
  const navigate = useNavigate();
  const [characters, setCharacters] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [charsData, foldersData] = await Promise.all([
        api.getCharacters(),
        api.getFolders()
      ]);
      setCharacters(charsData);
      setFolders(foldersData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const folder = await api.createFolder({ name: newFolderName });
      setFolders([...folders, folder]);
      setNewFolderName('');
      setShowNewFolder(false);
      toast.success('Folder created');
    } catch (error) {
      console.error('Create folder error:', error);
      toast.error(`Failed to create folder: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await api.deleteFolder(folderId);
      setFolders(folders.filter(f => f.id !== folderId));
      if (selectedFolder === folderId) setSelectedFolder(null);
      // Reload characters as they may have been moved
      const charsData = await api.getCharacters();
      setCharacters(charsData);
      toast.success('Folder deleted');
    } catch (error) {
      toast.error('Failed to delete folder');
    }
  };

  const handleDeleteCharacter = async (characterId) => {
    try {
      await api.deleteCharacter(characterId);
      setCharacters(characters.filter(c => c.id !== characterId));
      setSelectedCharacter(null);
      toast.success('Character deleted');
    } catch (error) {
      toast.error('Failed to delete character');
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const characterId = active.id;
    const targetId = over.id;

    // Check if dropping on a folder
    const isFolder = folders.some(f => f.id === targetId);
    const folderId = isFolder ? targetId : (targetId === 'no-folder' ? null : undefined);

    if (folderId !== undefined) {
      try {
        await api.moveCharacter(characterId, folderId);
        setCharacters(characters.map(c => 
          c.id === characterId ? { ...c, folderId } : c
        ));
        toast.success('Character moved');
      } catch (error) {
        toast.error('Failed to move character');
      }
    }
  };

  const handleImport = async (data) => {
    try {
      const result = await api.importCharacters(data);
      toast.success(result.message);
      loadData();
      setShowImport(false);
    } catch (error) {
      toast.error('Failed to import characters');
    }
  };

  const filteredCharacters = characters.filter(char => {
    const matchesSearch = char.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      char.type?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFolder = selectedFolder === null ? true : char.folderId === selectedFolder;
    return matchesSearch && matchesFolder;
  });

  const activeCharacter = characters.find(c => c.id === activeId);

  return (
    <div className="min-h-screen bg-[#0c0a09]" data-testid="dashboard">
      {/* Header */}
      <header className="border-b border-stone-800 bg-[#0c0a09]/95 backdrop-blur sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_campaign-deck-1/artifacts/zdwsmy37_Copy%20of%20Copy%20of%20Copy%20of%20Copy%20of%20BTT%20TEXT.png" 
                alt="Beyond The Table" 
                className="h-10 w-auto"
              />
              <h1 className="font-cinzel text-2xl font-bold text-amber-500 tracking-wider" data-testid="app-title">
                Camp
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                <Input
                  placeholder="Search characters..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64 bg-stone-900 border-stone-700 text-stone-200 placeholder:text-stone-500"
                  data-testid="search-input"
                />
              </div>
              <div className="flex border border-stone-700 rounded-md overflow-hidden">
                <Button
                  variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('grid')}
                  className="rounded-none"
                  data-testid="grid-view-btn"
                >
                  <LayoutGrid className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('list')}
                  className="rounded-none"
                  data-testid="list-view-btn"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowImport(true)}
                className="border-stone-700 text-stone-300 hover:bg-stone-800 hover:text-amber-500"
                data-testid="import-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import JSON
              </Button>
              <Button
                onClick={() => navigate('/builder')}
                className="bg-amber-700 hover:bg-amber-600 text-stone-100"
                data-testid="create-character-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Character
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-6">
            {/* Sidebar - Folders */}
            <aside className="w-64 flex-shrink-0">
              <div className="sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-cinzel text-sm font-semibold text-stone-400 uppercase tracking-wider">
                    Folders
                  </h2>
                  <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-stone-500 hover:text-amber-500" data-testid="new-folder-btn">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-stone-900 border-stone-700">
                      <DialogHeader>
                        <DialogTitle className="font-cinzel text-amber-500">New Folder</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <Input
                          placeholder="Folder name"
                          value={newFolderName}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          className="bg-stone-800 border-stone-700 text-stone-200"
                          data-testid="folder-name-input"
                        />
                        <Button onClick={handleCreateFolder} className="w-full bg-amber-700 hover:bg-amber-600" data-testid="create-folder-submit">
                          Create Folder
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                <ScrollArea className="h-[calc(100vh-200px)]">
                  <div className="space-y-1">
                    <FolderItem
                      id="no-folder"
                      name="All Characters"
                      icon={<LayoutGrid className="w-4 h-4" />}
                      isActive={selectedFolder === null}
                      onClick={() => setSelectedFolder(null)}
                      count={characters.filter(c => !c.folderId).length}
                    />
                    {folders.map(folder => (
                      <FolderItem
                        key={folder.id}
                        id={folder.id}
                        name={folder.name}
                        icon={<Folder className="w-4 h-4" />}
                        isActive={selectedFolder === folder.id}
                        onClick={() => setSelectedFolder(folder.id)}
                        onDelete={() => handleDeleteFolder(folder.id)}
                        count={characters.filter(c => c.folderId === folder.id).length}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </aside>

            {/* Main Content - Characters */}
            <main className="flex-1" data-testid="characters-grid">
              {isLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-stone-500">Loading...</div>
                </div>
              ) : filteredCharacters.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <div className="text-stone-500 mb-4">No characters found</div>
                  <Button onClick={() => navigate('/builder')} className="bg-amber-700 hover:bg-amber-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Character
                  </Button>
                </div>
              ) : (
                <div className={viewMode === 'grid' 
                  ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
                  : "space-y-3"
                }>
                  {filteredCharacters.map(character => (
                    <CharacterCard
                      key={character.id}
                      character={character}
                      viewMode={viewMode}
                      onClick={() => setSelectedCharacter(character)}
                      onEdit={() => navigate(`/builder/${character.id}`)}
                      onDelete={() => handleDeleteCharacter(character.id)}
                    />
                  ))}
                </div>
              )}
            </main>
          </div>

          <DragOverlay>
            {activeId && activeCharacter ? (
              <CharacterCard character={activeCharacter} viewMode="grid" isDragging />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Character Detail Modal */}
      {selectedCharacter && (
        <CharacterDetail
          character={selectedCharacter}
          onClose={() => setSelectedCharacter(null)}
          onEdit={() => {
            navigate(`/builder/${selectedCharacter.id}`);
            setSelectedCharacter(null);
          }}
          onDelete={() => handleDeleteCharacter(selectedCharacter.id)}
        />
      )}

      {/* Import Modal */}
      <ImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onImport={handleImport}
      />
    </div>
  );
}
